import PropTypes from "prop-types";
import { useState } from "react";
import { formatTwelveHrTime } from "helpers/formatting";
import Head from "next/head";
import Modal from "components/shared/stays/Modal";
import ModalBody from "components/shared/stays/ModalBody";
import Button from "solar/components/Button";
import Truncate from "solar/components/Truncate";
import Text from "components/shared/stays/Text";
import Title from "components/shared/stays/Title";
import Cancellation from "components/shared/stays/Cancellation";
import SectionRhythm from "components/shared/stays/SectionRhythm";
import styles from "components/stay/Questions.module.scss";

export default function Questions({
  cancellationPolicy,
  questions = {},
  checkInTime,
  checkOutTime,
}) {
  const [showModal, setShowModal] = useState(false);
  const handleModalShow = () => setShowModal(true);
  const handleModalClose = () => setShowModal(false);

  return (
    <>
      <Head>
        <script
          type="application/ld+json"
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "http://schema.org",
              "@type": "FAQPage",
              mainEntity: Object.entries(questions)
                .reverse()
                .map(([question, answer]) => ({
                  "@type": "Question",
                  name: question,
                  acceptedAnswer: {
                    type: "Answer",
                    text: answer,
                  },
                })),
            }),
          }}
        />
      </Head>
      <div className={styles.component}>
        <Title size={4}>Questions?</Title>
        <SectionRhythm>
          <div className={styles.grid}>
            {Object.entries(questions).map(([question, answer]) => (
              <div key={question}>
                <div className={styles.item}>
                  <Title size={5} className={styles.title}>
                    {question}
                  </Title>
                  <Text className={styles.text}>
                    <Truncate chars={200} more moreText="read more">
                      {answer}
                    </Truncate>
                  </Text>
                </div>
              </div>
            ))}
            {(checkInTime || checkOutTime) && (
              <div>
                <div className={styles.item}>
                  <Title size={5} className={styles.title}>
                    When&apos;s
                    {checkInTime && " check-in "}
                    {checkInTime && checkOutTime && "and"}
                    {checkOutTime && " check-out"}?
                  </Title>
                  <Text className={styles.text}>
                    <Truncate chars={200} more moreText="read more">
                      {checkInTime &&
                        `Check-in starts at ${formatTwelveHrTime(
                          checkInTime
                        )}. `}
                      {checkOutTime &&
                        `Check-out is at ${formatTwelveHrTime(
                          checkOutTime
                        )} the day of departure.`}
                    </Truncate>
                  </Text>
                </div>
              </div>
            )}
            <div className={styles.item}>
              <Cancellation
                title
                headerText="What's the cancellation policy?"
                policy={cancellationPolicy}
              />
            </div>
          </div>
        </SectionRhythm>
        {Object.entries(questions).length > 5 && (
          <>
            <SectionRhythm>
              <Button ghost responsive onClick={handleModalShow}>
                {"All "}
                {Object.entries(questions).length}
                {" questions"}
              </Button>
            </SectionRhythm>
            <Modal show={showModal} handleClose={handleModalClose}>
              <ModalBody>
                <Title className={styles.title}>
                  {"All "}
                  {Object.entries(questions).length}
                  {" questions"}
                </Title>
                <div className={styles.modal}>
                  {Object.entries(questions).map(([key, value]) => (
                    <div key={key} className={styles.item}>
                      <Title size={5} className={styles.title}>
                        {key}
                      </Title>
                      <Text className={styles.text}>{value}</Text>
                    </div>
                  ))}
                </div>
              </ModalBody>
            </Modal>
          </>
        )}
      </div>
    </>
  );
}

Questions.propTypes = {
  cancellationPolicy: PropTypes.string.isRequired,
  questions: PropTypes.objectOf(PropTypes.string).isRequired,
  checkInTime: PropTypes.string,
  checkOutTime: PropTypes.string,
};
